/**
 * Create account screen (T48 — provision + log in immediately; T49.3 — consent).
 *
 * Public email + password sign-up with the Turnstile bot challenge. On submit it
 * calls `useRegister` (→ POST /api/auth/register). T48: the backend now
 * PROVISIONS the account and issues a session in one step (parity with Google
 * sign-in) — so on success this applies the session (shared `useApplyAuthSuccess`)
 * and navigates straight into the app. There is no "check your inbox"
 * interstitial; email confirmation is a non-blocking in-app nudge surfaced later.
 *
 * T49.3 — explicit Terms + Privacy consent. A single unchecked-by-default indigo
 * consent row sits above the Create button and gates it: pressing Create without
 * the tick does NOT submit — it surfaces a calm `role="status"` hint (no red
 * error) and moves focus to the box (Designer spec). A valid submit passes
 * `termsAccepted: true`; the backend requires it and records the acceptance.
 *
 * An already-registered email returns a 409, surfaced here as an inline "account
 * exists → sign in / reset" message (a deliberate move away from the old
 * anti-enumeration silence, user-approved).
 *
 * Mirrors the LoginPage card layout (shared authLayout styles); web + native.
 */

import { useCallback, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useRegister } from '@shopista/shared/api/hooks';
import { apiErrorMessage, ApiError } from '@shopista/shared/api';
import { Button, Card, Checkbox, Input, Logo } from '../../components';
import { useSession, type WebTokenStorage } from '../../auth';
import { routes } from '../../routes/paths';
import { isNativePlatform } from '../../routes/nativeGoogleSignIn';
import { isAndroidBrowser } from '../../lib/platform';
import { redirectToApp } from '../../lib/appIntent';
import { useApplyAuthSuccess } from './useApplyAuthSuccess';
import { TurnstileWidget } from './TurnstileWidget';
import { LegalConsentOverlay } from './LegalConsentOverlay';
import type { LegalSlug } from '../legal/content';
import {
  authPageStyle,
  authStackStyle,
  authWordmarkRowStyle,
  authCardBodyStyle,
  authHeadingStyle,
  authSubheadingStyle,
  authFormStyle,
  authInlineErrorStyle,
  authFooterRowStyle,
  authLinkStyle,
  authConsentRowStyle,
  authConsentHintStyle,
  authConsentLinkStyle,
  consentLinkButtonReset,
  EMAIL_PATTERN,
  MIN_PASSWORD_LENGTH,
} from './authLayout';

const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';
// The calm, non-red nudge for an unticked consent attempt (Designer spec §2).
const CONSENT_HINT = 'Just tick the box above and you’re in.';
const CONSENT_HINT_ID = 'create-account-consent-hint';

export interface CreateAccountPageProps {
  tokens: WebTokenStorage;
}

export function CreateAccountPage({ tokens }: CreateAccountPageProps): JSX.Element | null {
  // Guest-only: an already-authenticated session shouldn't see the sign-up form.
  const session = useSession({ tokens });

  const register = useRegister();
  const applyAuthSuccess = useApplyAuthSuccess(tokens);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  // Set on a 409 (account already exists) so the inline branch can offer the
  // sign-in / reset paths rather than a bare error line.
  const [accountExists, setAccountExists] = useState(false);
  // T49.3 — consent state. Unticked by default (a pre-tick is not valid consent
  // under PIPEDA). `consentNudge` drives the calm hint + the box's attention ring.
  const [consent, setConsent] = useState(false);
  const [consentNudge, setConsentNudge] = useState(false);
  const consentRef = useRef<HTMLInputElement | null>(null);
  // BUG42 — which legal doc the in-app overlay is showing (null = closed). The
  // overlay renders OVER this screen so the form stays mounted (state preserved);
  // focus returns to the button that opened it, tracked per-slug below.
  const [legalOverlaySlug, setLegalOverlaySlug] = useState<LegalSlug | null>(null);
  const termsButtonRef = useRef<HTMLButtonElement | null>(null);
  const privacyButtonRef = useRef<HTMLButtonElement | null>(null);
  // The trigger to return focus to on close — the button matching the open slug.
  const activeTriggerRef = legalOverlaySlug === 'privacy' ? privacyButtonRef : termsButtonRef;

  // Stable so TurnstileWidget's effect deps don't churn.
  const handleToken = useCallback((token: string) => setTurnstileToken(token), []);

  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
  // When a site key IS configured, require a solved token before enabling submit.
  // When it's NOT (local stand), the widget reports a non-empty placeholder token
  // that the backend's off-production bypass accepts, so don't block on the token.
  const turnstileReady = siteKey ? turnstileToken.length > 0 : true;

  const onSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      setFormError(null);
      setAccountExists(false);
      // T49.3 — consent gates the Create action. An unticked attempt does NOT
      // submit: show the calm hint (not a red error), pull focus to the box, and
      // pulse the attention ring. The button stays enabled-looking so the user
      // learns the one remaining step rather than hitting a silent dead-end.
      if (!consent) {
        setConsentNudge(true);
        consentRef.current?.focus();
        return;
      }
      // Trim before validating + sending: mobile keyboards routinely append a
      // trailing space after an email autofill/suggestion, which would fail the
      // pattern on an otherwise-valid address. The backend also lowercases+trims.
      const normalizedEmail = email.trim();
      if (!EMAIL_PATTERN.test(normalizedEmail)) {
        setFormError(INVALID_EMAIL_MESSAGE);
        return;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirm) {
        setFormError('Passwords do not match.');
        return;
      }
      const native = isNativePlatform();
      register.mutate(
        native
          ? { email: normalizedEmail, password, turnstileToken, termsAccepted: true, native: true }
          : { email: normalizedEmail, password, turnstileToken, termsAccepted: true },
        {
          // T48 — register issues a session; apply it and enter the app directly.
          onSuccess: applyAuthSuccess,
          onError: (err) => {
            if (err instanceof ApiError && err.status === 409) {
              // Account already exists — show the sign-in / reset branch.
              setAccountExists(true);
              return;
            }
            if (err instanceof ApiError && err.status === 403) {
              setFormError('Bot challenge failed. Please try again.');
              return;
            }
            if (err instanceof ApiError && err.status === 429) {
              setFormError('Too many attempts. Please try again later.');
              return;
            }
            if (err instanceof ApiError && err.status === 400) {
              setFormError(INVALID_EMAIL_MESSAGE);
              return;
            }
            setFormError(apiErrorMessage(err, 'Could not create your account.'));
          },
        },
      );
    },
    [email, password, confirm, turnstileToken, consent, register, applyAuthSuccess],
  );

  if (session.status === 'authenticated') {
    return <Navigate to={routes.home} replace />;
  }
  if (session.status === 'hydrating') {
    // Session still resolving (`/auth/me` in flight). Hold — do NOT render the
    // form and do NOT fire the funnel yet: an authenticated user must resolve to
    // the home redirect above, never get funnelled to the intent mid-hydration.
    // Once resolved this re-renders into the authenticated redirect or the guest
    // branch below.
    return <main data-testid="create-account-page" style={authPageStyle} />;
  }

  // T27.2.11 Part B — mobile sign-up funnel. A GUEST on a mobile Android browser
  // is routed INTO the app (open the app if installed, else Play) instead of the
  // browser sign-up form. Placed AFTER the authenticated guard above, so an
  // authenticated user still redirects home (never to the intent); it reaches
  // here only when the session is not authenticated. Firing here — before the
  // card renders — means there is no form flash. Gated on `isAndroidBrowser()`,
  // so desktop / iOS / inside the native app all fall through to the form.
  if (isAndroidBrowser()) {
    redirectToApp();
    return null;
  }

  return (
    <main style={authPageStyle} data-testid="create-account-page">
      <div style={authStackStyle}>
        <div style={authWordmarkRowStyle}>
          <Logo size={80} showText data-testid="create-account-logo" />
        </div>

        <Card padding="lg" shadow="md" data-testid="create-account-card">
          <div style={authCardBodyStyle}>
            <h1 style={authHeadingStyle}>Create your account</h1>
            <p style={authSubheadingStyle}>
              Sign up with an email and password — no Google account needed.
            </p>

            <form
              style={authFormStyle}
              onSubmit={onSubmit}
              data-testid="create-account-form"
              noValidate
            >
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="create-account-email"
                required
              />
              <Input
                label="Password"
                type="password"
                name="new-password"
                autoComplete="new-password"
                reveal
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="create-account-password"
                required
              />
              <Input
                label="Confirm password"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                reveal
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="create-account-confirm"
                required
              />

              <TurnstileWidget onToken={handleToken} />

              {/* T49.3 — the consent row, low in the card so it gates the action
                  below it. Unticked by default; ticking clears any active nudge. */}
              <div style={authConsentRowStyle} data-testid="create-account-consent-row">
                <Checkbox
                  ref={consentRef}
                  checked={consent}
                  nudge={consentNudge}
                  describedById={consentNudge ? CONSENT_HINT_ID : undefined}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (e.target.checked) setConsentNudge(false);
                  }}
                  data-testid="create-account-consent"
                  label={
                    // BUG42 — open the legal docs in an IN-APP overlay over this
                    // screen (LegalConsentOverlay), NOT a router nav or a new tab.
                    // A <Link> SPA-navigated away and unmounted this form (dropping
                    // the entered email/password/consent); a bare target="_blank"
                    // anchor never opened in the Android WebView. A button that
                    // opens the overlay keeps this form MOUNTED on web AND native,
                    // so all field state survives. stopPropagation keeps the tap
                    // off the checkbox toggle; the button is styled to look like
                    // the previous inline link.
                    <>
                      I agree to the{' '}
                      <button
                        ref={termsButtonRef}
                        type="button"
                        style={{ ...authConsentLinkStyle, ...consentLinkButtonReset }}
                        data-testid="create-account-consent-terms"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLegalOverlaySlug('terms');
                        }}
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        ref={privacyButtonRef}
                        type="button"
                        style={{ ...authConsentLinkStyle, ...consentLinkButtonReset }}
                        data-testid="create-account-consent-privacy"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLegalOverlaySlug('privacy');
                        }}
                      >
                        Privacy Policy
                      </button>
                      .
                    </>
                  }
                />
                {consentNudge && (
                  <p
                    id={CONSENT_HINT_ID}
                    role="status"
                    style={authConsentHintStyle}
                    data-testid="create-account-consent-hint"
                  >
                    {CONSENT_HINT}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={register.isPending || !turnstileReady}
                data-testid="create-account-submit"
              >
                {register.isPending ? 'Creating account…' : 'Create account'}
              </Button>

              {accountExists ? (
                <p
                  role="alert"
                  data-testid="create-account-exists"
                  style={authInlineErrorStyle}
                >
                  An account already exists for that email.{' '}
                  <Link to={routes.login} style={authLinkStyle} data-testid="create-account-exists-signin">
                    Sign in
                  </Link>{' '}
                  or{' '}
                  <Link to={routes.resetRequest} style={authLinkStyle} data-testid="create-account-exists-reset">
                    reset your password
                  </Link>
                  .
                </p>
              ) : (
                formError && (
                  <p
                    role="alert"
                    data-testid="create-account-error"
                    style={authInlineErrorStyle}
                  >
                    {formError}
                  </p>
                )
              )}
            </form>
          </div>
        </Card>

        <p style={authFooterRowStyle}>
          Already have an account?{' '}
          <Link to={routes.login} style={authLinkStyle} data-testid="create-account-signin-link">
            Sign in
          </Link>
        </p>
      </div>

      {/* BUG42 — the in-app legal overlay. It portals over this screen (via the
          shared Modal) so opening it never unmounts this form; closing returns
          focus to the consent button that opened it. */}
      <LegalConsentOverlay
        slug={legalOverlaySlug}
        onClose={() => setLegalOverlaySlug(null)}
        triggerRef={activeTriggerRef}
      />
    </main>
  );
}
